import { SumexXmlVersion } from "./sumex-xml-version";

export interface MappedSumexError {
	message: string;
	/** Raw failure text from the Sumex module, kept verbatim for support. */
	detail: string;
	/** False when no known cause matched — the message is then generic, never invented. */
	isMapped: boolean;
	/** Sumex field the cause lives in, so it can be flagged like a preflight problem. */
	wireField?: string;
}

const ALLOWED_ARTICLE_CODES =
	"2100.0, 2130.0, 2150.0, 2200.0, 2300.0, 2400.0, 2900.0, 3100.0, 3110.0, 3170.0, 3500.0 oder 3700.0";

interface SumexErrorRule {
	match: RegExp;
	message: (version: SumexXmlVersion, detail: string) => string;
	wireField?: string;
}

const RULES: SumexErrorRule[] = [
	{
		match: /xsd|schema validation|validation failed/,
		message: (version) =>
			`Das erzeugte XML entspricht nicht dem Standard «${
				version === SumexXmlVersion.V500 ? "5.00" : "4.50"
			}».`,
	},
	{
		match: /code not valid|invalid code|unknown code|tariff/,
		// The service reports the offending code, so name it rather than making the
		// user check every position by hand.
		message: (_version, detail) => {
			const code = /code not valid:\s*(\S+)/i.exec(detail)?.[1];
			return code
				? `Die Position mit dem Code «${code}» ist ungültig. Bei Artikeln muss der Cluster-Code ${ALLOWED_ARTICLE_CODES} lauten.`
				: `Eine Position hat einen ungültigen Code. Bei Artikeln muss der Cluster-Code ${ALLOWED_ARTICLE_CODES} lauten.`;
		},
	},
	{
		match: /gln/,
		message: () =>
			"Die GLN des Labors ist ungültig. Prüfen Sie sie in den Organisationseinstellungen.",
	},
	{
		match: /phone|telephone|telefon|fax/,
		message: () =>
			"Die Telefonnummer ist ungültig (korrektes Format: 031 302 18 72).",
	},
	{
		match: /birth ?date|geburtsdatum/,
		message: () =>
			"Das Geburtsdatum des Patienten fehlt oder ist ungültig. Ergänzen Sie es in den Patientendaten.",
		wireField: "PATBirthDate",
	},
	{
		match: /zsr/,
		message: () =>
			"Die ZSR-Nummer des Labors wurde vom Sumex-Modul abgelehnt. Prüfen Sie die Organisationseinstellungen.",
	},
	{
		match: /vat|mwst/,
		message: () =>
			"Die MwSt-Nummer aus der Region des Kunden wurde vom Sumex-Modul abgelehnt. Prüfen Sie die Region in den Kundeneinstellungen.",
		wireField: "LABVATNumber",
	},
	// Failures of the backend proxy itself, not of the Sumex module.
	{
		match: /^unauthorized$/,
		message: () =>
			"Ihre Sitzung ist abgelaufen. Bitte melden Sie sich neu an und versuchen Sie es erneut.",
	},
	{
		match: /xml service unreachable|xml service not configured/,
		message: () =>
			"Der XML-Dienst ist zurzeit nicht erreichbar. Bitte versuchen Sie es in einem Moment erneut.",
	},
];

const GENERIC_MESSAGE =
	"Das XML konnte nicht erstellt werden. Die Rückmeldung des Sumex-Moduls finden Sie unten.";

const TRANSPORT_MESSAGE =
	"Der XML-Dienst ist nicht erreichbar. Bitte versuchen Sie es in einem Moment erneut.";

/**
 * Turns a Sumex/microservice failure into an actionable German message. Unmapped
 * causes get a generic sentence plus the raw detail, never a fabricated reason;
 * "Server überlastet" is reserved for real transport failures.
 */
export const mapSumexError = (
	detail: string,
	options: { version?: SumexXmlVersion; isTransportFailure?: boolean } = {}
): MappedSumexError => {
	const { version = SumexXmlVersion.V450, isTransportFailure = false } =
		options;
	const raw = detail ?? "";

	if (isTransportFailure) {
		return { message: TRANSPORT_MESSAGE, detail: raw, isMapped: true };
	}

	const haystack = raw.toLowerCase();
	const rule = RULES.find((candidate) => candidate.match.test(haystack));

	if (!rule) {
		return { message: GENERIC_MESSAGE, detail: raw, isMapped: false };
	}

	return {
		message: rule.message(version, raw),
		detail: raw,
		isMapped: true,
		wireField: rule.wireField,
	};
};

/**
 * Three response shapes are possible during the rollout: the new `{ error }` body,
 * the legacy ASP.NET `{ errors: {...} }` body, and no response at all.
 */
export const extractServiceErrorDetail = (error: unknown): string => {
	const fallback =
		error instanceof Error
			? error.message
			: String(error ?? "Unbekannter Fehler");

	const data = (error as { response?: { data?: unknown } } | undefined)
		?.response?.data;

	if (typeof data === "string" && data.trim()) {
		return data;
	}

	if (data && typeof data === "object") {
		const structured = (data as { error?: unknown }).error;
		if (typeof structured === "string" && structured.trim()) {
			return structured;
		}

		const legacy = (data as { errors?: unknown }).errors;
		if (legacy && typeof legacy === "object") {
			const first = Object.values(legacy as Record<string, unknown>)[0];
			if (Array.isArray(first) && first.length) return String(first[0]);
			if (typeof first === "string" && first.trim()) return first;
		}
	}

	return fallback;
};

export const buildSumexSupportMessage = ({
	detail,
	version,
	endpoint,
	jobCode,
	documentNumber,
}: {
	detail: string;
	version: SumexXmlVersion;
	endpoint: string;
	jobCode?: string | null;
	documentNumber?: string | null;
}): string => {
	const context = [
		jobCode && `Auftrag: ${jobCode}`,
		documentNumber && `Dokument: ${documentNumber}`,
		`XML-Version: ${version}`,
		`Endpunkt: ${endpoint}`,
	]
		.filter(Boolean)
		.join("\n");

	return `Hallo Support-Team

beim Erstellen des SUVA-XML ist ein Fehler aufgetreten.

${context}

Fehlermeldung:
${detail || "(keine Details)"}

Vielen Dank für die Hilfe!`;
};
